import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, X, MessageSquare } from 'lucide-react';

/**
 * Pinned chat input bar — sits at the bottom of the canvas.
 *
 * Behaviour:
 * - If a node is selected: sends prompt as a child of that node (operation = 'prompt')
 * - If no node selected: creates a new root input node with the text
 * - Enter to submit, Shift+Enter for newline
 * - Escape clears selection / closes
 */
const CanvasChatBar = ({ selectedNode, onSubmit, onClearSelection, prefill, onPrefillConsumed }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  // Auto-focus when a node is selected
  useEffect(() => {
    if (selectedNode && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [selectedNode?.id]);

  // Apply prefill when set (e.g. clicking a prompt node to edit it)
  useEffect(() => {
    if (prefill) {
      setText(prefill);
      if (onPrefillConsumed) onPrefillConsumed();
      textareaRef.current?.focus();
    }
  }, [prefill]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'Escape') {
        setText('');
        if (onClearSelection) onClearSelection();
      }
    },
    [text, selectedNode],
  );

  const handleSubmit = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({ text: trimmed, targetNode: selectedNode || null });
      setText('');
    } finally {
      setSubmitting(false);
      textareaRef.current?.focus();
    }
  }, [text, selectedNode, submitting, onSubmit]);

  const targetLabel = selectedNode
    ? (selectedNode.data?.title || selectedNode.data?.content?.slice(0, 40) || `Node ${selectedNode.id}`)
    : null;

  return (
    <div className="canvas-chat-bar">
      {targetLabel && (
        <div className="canvas-chat-bar-target">
          <MessageSquare size={12} />
          <span>Replying to: <strong>{targetLabel}</strong></span>
          <button
            className="canvas-chat-bar-clear"
            onClick={() => { setText(''); if (onClearSelection) onClearSelection(); }}
            title="Clear selection"
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div className="canvas-chat-bar-input-row">
        <textarea
          ref={textareaRef}
          className="canvas-chat-bar-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          placeholder={
            selectedNode
              ? 'Type a prompt for this node… (Enter to send, Shift+Enter for newline)'
              : 'Type a prompt to start… or drag a file / tool onto the canvas'
          }
          rows={1}
          disabled={submitting}
        />
        <button
          className={`canvas-chat-bar-send${submitting ? ' sending' : ''}${!text.trim() ? ' disabled' : ''}`}
          onClick={handleSubmit}
          disabled={!text.trim() || submitting}
          title="Send (Enter)"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default CanvasChatBar;
